import PermissionService from 'components/Common/PermissionService';
import { DeleteFlowListContext } from 'pages/Flow/DeleteFlowListContextProvider';
import { FlowContext } from 'pages/Flow/FlowContextProvider';
import { FlowVersionHistoryContext } from 'pages/Flow/FlowRun/FlowVersionHistoryProvider';
import { OWNERSHIP_RESTRICTION_TOOLTIP } from 'pages/Flow/constants';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import { cleanRoom } from 'utils/spaUrls';
import { cleanRoomPermissions, productPerm } from 'utils/appConstants';
import { crPrivacyType } from 'components/CleanRooms/Wizard/constants';
import { updateSelectedFlow } from 'redux/actions/FlowAction';
import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';


const PROVISIONED_DELETE_RESTRICTION_TOOLTIP = 'This Flow cannot be deleted because it is currently provisioned. To remove it, you must deprovision it.';

const getDeleteTooltip = ({ isProvisioned, isFlowOwnedByCurrentUserOrg }) => {
  if (isProvisioned) {
    return PROVISIONED_DELETE_RESTRICTION_TOOLTIP;
  }
  if (!isFlowOwnedByCurrentUserOrg) {
    return OWNERSHIP_RESTRICTION_TOOLTIP;
  }
  return '';
};

export const useActionItems = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { ID: orgId } = useSelector(activeOrgSelector);
  const crDetail = useSelector(state => state.cleanRooms.detail);
  const { setDeleteFlowDescription } = useContext(DeleteFlowListContext);
  const { handleAdvancedSettingOpen } = useContext(FlowContext);
  const { openModal: openVersionModal } = useContext(FlowVersionHistoryContext);
  const { crId } = useParams();

  const canBuildFlows = PermissionService.isAuthorized([cleanRoomPermissions.crFlowsBuilder]);
  const canViewFlows = PermissionService.isAuthorized([cleanRoomPermissions.crViewOnlyFlows]);
  const canViewVersions = PermissionService.isAuthorized([
    cleanRoomPermissions.crFlowsBuilder,
    cleanRoomPermissions.crManageFlowRuns,
    cleanRoomPermissions.manageDatasets,
  ]);
  const isDifferentialPrivacyEnabled = PermissionService
    .isProductEnabled([productPerm.DifferentialPrivacy]);
  const customDiffPrivacyCleanRoom = crDetail.cleanRoomPrivate
    && crDetail.cleanRoomPrivacyType === crPrivacyType.CUSTOM_DIFFERENTIAL_PRIVACY;

  return ({ details }) => {
    const { isProvisioned = false } = details;
    const isFlowOwnedByCurrentUserOrg = details.flow?.organizationID === orgId;

    const items = [];
    if (canViewFlows) {
      items.push({
        label: 'View',
        order: 0,
        action: () => {
          dispatch(updateSelectedFlow({ flowID: details.ID }));
          history.push(cleanRoom.flowView(crId, details.ID, details.flow?.ID));
        },
      });
    }

    if (canBuildFlows) {
      items.push({
        label: 'Edit',
        order: 1,
        disabled: !isFlowOwnedByCurrentUserOrg,
        tooltipTitle: !isFlowOwnedByCurrentUserOrg ? OWNERSHIP_RESTRICTION_TOOLTIP : '',
        action: () => {
          dispatch(updateSelectedFlow({ flowID: details.ID }));
          history.push(cleanRoom.flowBuilder(crId, details.ID, details.flow?.ID));
        },
      });
      items.push({
        label: 'Delete',
        order: 2,
        disabled: !isFlowOwnedByCurrentUserOrg || isProvisioned,
        tooltipTitle: getDeleteTooltip({ isProvisioned, isFlowOwnedByCurrentUserOrg }),
        action: () => {
          setDeleteFlowDescription({
            ID: details.flowID,
            name: details.flow?.name,
            displayID: details.flow?.displayID,
            crID: details.cleanRoomID,
          });
        },
      });
      if (isDifferentialPrivacyEnabled && customDiffPrivacyCleanRoom) {
        items.push({
          label: 'Advanced Settings',
          order: 4,
          action: () => {
            handleAdvancedSettingOpen({
              crID: details.cleanRoomID,
              cleanRoomFlowID: details.ID,
              name: details.flow?.name,
            });
          },
        });
      }
    }

    if (canViewVersions) {
      items.push({
        label: 'See Version History',
        order: 3,
        action: () => {
          openVersionModal(details);
        },
      });
    }

    // Return new items sorted ascending by order field (immutability)
    return [...items].sort((a, b) => a.order - b.order);
  };
};
